import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useSsoConnectionStatusQuery } from '@/api/api-hooks';
import { DefaultSourceVisibilitySettings } from '@/components/Settings/DefaultSourceVisibilitySettings';
import { OrganizationInfo } from '@/components/Settings/OrganizationInfo';
import { PatAdminSettings } from '@/components/Settings/PatAdminSettings';
import { SettingsAccordion, SettingsAccordionSection } from '@/components/Settings/SettingsAccordion';
import { SettingsPageLayout } from '@/components/Settings/SettingsPageLayout';
import { ScimSection } from '@/components/Settings/SsoScim/ScimSection';
import { SsoSection } from '@/components/Settings/SsoScim/SsoSection';
import { TagsList } from '@/components/TagsList/TagsList';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useContextMode } from '@/hooks/useContextMode';
import { useUser } from '@/hooks/useUser';

export const Route = createFileRoute('/$organizationName/settings/organization')({
  component: OrganizationPage,
});

function OrganizationPage() {
  const { isSuperAdmin, organizationDisplayName } = useUser();
  // Tokens are for MCP and API access to generated context, so their policy
  // goes in LITE as the Access Tokens page does.
  const isContextLight = useContextMode() === 'LITE';

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Settings`;
  }, [organizationDisplayName]);

  const sections: SettingsAccordionSection[] = [
    { value: 'organization-info', title: 'Organization Info', children: <OrganizationInfo /> },
  ];

  if (isSuperAdmin) {
    sections.push({
      value: 'default-source-visibility',
      title: 'Default Source Visibility',
      children: <DefaultSourceVisibilitySettings />,
    });
    if (!isContextLight) {
      sections.push({
        value: 'personal-access-tokens',
        title: 'Personal Access Tokens',
        children: <PatAdminSettings />,
      });
    }
    sections.push({
      value: 'sso-scim',
      title: 'SSO & SCIM',
      children: <SsoScimSectionContent />,
    });
  }

  sections.push({
    value: 'tags',
    title: 'Tags',
    children: <TagsList />,
  });

  return (
    <SettingsPageLayout title="Organization" subTitle="Manage your settings">
      <SettingsAccordion sections={sections} />
    </SettingsPageLayout>
  );
}

// Page-specific helper — co-located because it owns SSO/SCIM data fetching tied to this section.
// (AGENTS.md §one-component-per-file: helpers like this with tight page coupling are an accepted exception.)
function SsoScimSectionContent() {
  const { data, isError, isLoading } = useSsoConnectionStatusQuery();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (isError || !data) {
    return <div className="text-destructive text-sm">Failed to load SSO/SCIM status. Please try again later.</div>;
  }
  return (
    <div className="space-y-6">
      <SsoSection status={data} />
      <Separator />
      <ScimSection status={data} />
    </div>
  );
}
